import { emptySplitApi as api } from "./baseApi";

export const addTagTypes = ["CastingToolController"] as const;

// Types for Casting Tool
export interface CastingToolEntries {
  // Section 1: Best Performance - Better
  alert: string;
  curious: string;
  responsive: string;
  resourceful: string;
  // Section 2: Best Results - Bigger
  faster: string;
  easier: string;
  bigger: string;
  better: string;
  // Section 3: Being a Hero
  newCommitment: string;
  newCourage: string;
  newCapability: string;
  newConfidence: string;
  // Section 4: Not Driving Others Crazy
  showUpOnTime: string;
  doWhatYouSay: string;
  finishWhatYouStart: string;
  sayPleaseThankYou: string;
}

export interface CastingTool {
  id: string;
  userId: string;
  reviewPeriod: string;
  entries: CastingToolEntries;
  createdAt: string;
  updatedAt: string;
}

export interface CastingToolWithUser extends CastingTool {
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    department?: string;
    designation?: string;
  };
}

export interface DepartmentCastingStats {
  department: string;
  totalEmployees: number;
  completedThisMonth: number;
  completionRate: number;
  reviewPeriod: string;
}

export interface DepartmentEmployee {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  designation?: string;
  hasSubmitted: boolean;
  castingTool: CastingTool | null;
  reviewPeriod: string;
}

// Create/Update data types
export interface NewCastingTool {
  reviewPeriod: string;
  entries: CastingToolEntries;
}

export interface UpdateCastingTool {
  entries?: CastingToolEntries;
  reviewPeriod?: string;
}

// Casting Tool API endpoints
const castingToolApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Create a new casting tool entry
      postCastingTool: build.mutation<CastingTool, { castingToolData: NewCastingTool }>({
        query: (queryArg) => ({
          url: `/casting-tool`,
          method: "POST",
          body: queryArg.castingToolData,
        }),
        invalidatesTags: ["CastingToolController"],
      }),

      // Update an existing casting tool entry
      patchCastingToolById: build.mutation<CastingTool, { id: string; castingToolData: UpdateCastingTool }>({
        query: (queryArg) => ({
          url: `/casting-tool/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.castingToolData,
        }),
        invalidatesTags: ["CastingToolController"],
      }),

      // Get casting tool entry by ID
      getCastingToolById: build.query<CastingTool, { id: string }>({
        query: (queryArg) => ({
          url: `/casting-tool/${queryArg.id}`,
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get my casting tool entries
      getMyCastingTools: build.query<CastingTool[], void>({
        query: () => ({
          url: `/casting-tools/my`,
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get casting tool entries by user ID
      getCastingToolsByUserId: build.query<CastingTool[], { userId: string }>({
        query: (queryArg) => ({
          url: `/casting-tools-by-user/${queryArg.userId}`,
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get casting tool entry for current user by review period
      getCastingToolByPeriod: build.query<CastingTool | null, { reviewPeriod: string }>({
        query: (queryArg) => ({
          url: `/casting-tool/period/${encodeURIComponent(queryArg.reviewPeriod)}`,
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get all casting tool entries (admin only)
      getAllCastingTools: build.query<CastingToolWithUser[], { reviewPeriod?: string } | void>({
        query: (queryArg) => ({
          url: `/casting-tools/all`,
          params: queryArg?.reviewPeriod ? { reviewPeriod: queryArg.reviewPeriod } : {},
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get department statistics
      getCastingToolDepartmentStats: build.query<DepartmentCastingStats[], { reviewPeriod?: string } | void>({
        query: (queryArg) => ({
          url: `/casting-tools/department-stats`,
          params: queryArg?.reviewPeriod ? { reviewPeriod: queryArg.reviewPeriod } : {},
        }),
        providesTags: ["CastingToolController"],
      }),

      // Get detailed department view
      getCastingToolDepartmentDetail: build.query<DepartmentEmployee[], { department: string; reviewPeriod?: string }>({
        query: (queryArg) => ({
          url: `/casting-tools/department/${encodeURIComponent(queryArg.department)}`,
          params: queryArg.reviewPeriod ? { reviewPeriod: queryArg.reviewPeriod } : {},
        }),
        providesTags: ["CastingToolController"],
      }),
    }),
    overrideExisting: true,
  });

export { castingToolApi };

export const {
  usePostCastingToolMutation,
  usePatchCastingToolByIdMutation,
  useGetCastingToolByIdQuery,
  useLazyGetCastingToolByIdQuery,
  useGetMyCastingToolsQuery,
  useLazyGetMyCastingToolsQuery,
  useGetCastingToolsByUserIdQuery,
  useLazyGetCastingToolsByUserIdQuery,
  useGetCastingToolByPeriodQuery,
  useLazyGetCastingToolByPeriodQuery,
  useGetAllCastingToolsQuery,
  useLazyGetAllCastingToolsQuery,
  useGetCastingToolDepartmentStatsQuery,
  useLazyGetCastingToolDepartmentStatsQuery,
  useGetCastingToolDepartmentDetailQuery,
  useLazyGetCastingToolDepartmentDetailQuery,
} = castingToolApi;
